import { useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import PageContainer from "../../../components/container/PageContainer";

import Breadcrumb from "../../../layouts/full-layout/breadcrumb/Breadcrumb";

import CustomFormLabel from "../../../components/forms/custom-elements/CustomFormLabel";

import CustomTextField from "../../../components/forms/custom-elements/CustomTextField";

import FeatherIcon from "feather-icons-react";

import SearchIcon from "@mui/icons-material/Search";

import CloseIcon from "@mui/icons-material/Close";

import FilterAltIcon from "@mui/icons-material/FilterAlt";

import EditIcon from "@mui/icons-material/Edit";

import { useFormik } from "formik";

import * as Yup from "yup";

import { toast } from "react-toastify";

import {
  AddOrEditShift,
  AddOrEditShiftWiseRuleConfiguration,
} from "../../../services/asaiadmin";

import { GetAllDepartments } from "../../../services/admin";
import { GetAllEmployeeDetailsDropdowns } from "../../../services/human-resource";

/* =========================================================

   EMPTY FORM

========================================================= */

const EMPTY_FORM = {
  departmentId: "",

  shiftId: "",

  lateMinutes: "",

  minimumFullDayMinutes: "",

  minimumHalfDayMinutes: "",

  shortLeaveMinutes: "",

  overtimeAfterMinutes: "",

  effectiveFrom: new Date().toISOString().slice(0, 10),

  effectiveTo: "",
};

/* =========================================================

   RULE TABLE COLUMNS

========================================================= */

const RULE_COLUMNS = [
  { field: "departmentName", label: "Department" },

  { field: "shiftName", label: "Shift" },

  { field: "lateMinutes", label: "Late Minutes", numeric: true },

  {
    field: "minimumFullDayMinutes",

    label: "Minimum Full Day",

    numeric: true,
  },

  {
    field: "minimumHalfDayMinutes",

    label: "Minimum Half Day",

    numeric: true,
  },

  {
    field: "shortLeaveMinutes",

    label: "Short Leave Minutes",

    numeric: true,
  },

  {
    field: "overtimeAfterMinutes",

    label: "Overtime After",

    numeric: true,
  },

  { field: "effectiveFrom", label: "Effective From" },

  { field: "effectiveTo", label: "Effective To" },
];

/* =========================================================

   BASIC HELPERS

========================================================= */

const normalizeText = (value) =>
  String(value ?? "")
    .trim()

    .toLowerCase()

    .replace(/\s+/g, "");

const getDepartmentId = (item) =>
  item?.departmentId ??
  item?.DepartmentId ??
  item?.departmentID ??
  item?.DepartmentID ??
  item?.department_id ??
  item?.deptId ??
  item?.DeptId ??
  item?.deptID ??
  item?.DeptID ??
  "";

const getDepartmentOptionId = (item) =>
  getDepartmentId(item) || item?.id || item?.Id || item?.ID || "";

const getDepartmentName = (item) =>
  item?.departmentName ??
  item?.DepartmentName ??
  item?.department_name ??
  item?.deptName ??
  item?.DeptName ??
  item?.name ??
  item?.Name ??
  "";

const getShiftId = (item) =>
  item?.shiftId ??
  item?.ShiftId ??
  item?.shiftID ??
  item?.ShiftID ??
  item?.shiftMasterId ??
  item?.ShiftMasterId ??
  item?.id ??
  item?.Id ??
  item?.ID ??
  "";

const getRuleId = (item) =>
  item?.id ??
  item?.Id ??
  item?.ID ??
  item?.ruleId ??
  item?.RuleId ??
  item?.shiftWiseRuleId ??
  item?.ShiftWiseRuleId ??
  item?.shiftWiseRuleConfigurationId ??
  item?.ShiftWiseRuleConfigurationId ??
  "";

const getResponseRows = (response) => {
  const candidates = [
    response?.data?.data,

    response?.data?.Data,

    response?.data?.result,

    response?.data?.Result,

    response?.data?.results,

    response?.data,

    response?.result,

    response?.Result,
  ];

  return candidates.find(Array.isArray) ?? [];
};

const getApiResult = (response) => {
  const candidates = [
    response?.data?.data,

    response?.data?.Data,

    response?.data?.result,

    response?.data?.Result,

    response?.data,
  ];

  for (const value of candidates) {
    if (Array.isArray(value)) return value[0] ?? {};

    if (value && typeof value === "object") return value;
  }

  return {};
};

const getApiMessage = (value, depth = 0) => {
  if (depth > 5 || value == null) return "";

  if (typeof value === "string") return value.trim();

  if (Array.isArray(value)) {
    for (const entry of value) {
      const message = getApiMessage(entry, depth + 1);

      if (message) return message;
    }

    return "";
  }

  if (typeof value !== "object") return "";

  for (const key of [
    "message",

    "Message",

    "errorMessage",

    "ErrorMessage",

    "detail",

    "Detail",

    "title",

    "Title",
  ]) {
    const message = value[key];

    if (typeof message === "string" && message.trim()) {
      return message.trim();
    }
  }

  for (const key of [
    "errors",

    "Errors",

    "error",

    "Error",

    "data",

    "Data",

    "result",

    "Result",
  ]) {
    const message = getApiMessage(value[key], depth + 1);

    if (message) return message;
  }

  return "";
};

const isApiFailure = (result) => {
  if (!result || typeof result !== "object") return false;

  for (const key of [
    "success",

    "Success",

    "isSuccess",

    "IsSuccess",

    "succeeded",

    "Succeeded",
  ]) {
    if (result[key] === false) return true;
  }

  const status = result.status ?? result.Status;

  if (
    typeof status === "string" &&
    /^(error|failed|failure|false)$/i.test(status.trim())
  ) {
    return true;
  }

  if (status === 0) return true;

  return /already exists|duplicate|\bfailed\b|\bfailure\b|\bunable\b|\binvalid\b|\bexception\b/i.test(
    getApiMessage(result),
  );
};

const getRequestErrorMessage = (error, fallback) =>
  getApiMessage(error?.response?.data) || error?.message || fallback;

const getDateValue = (value) => {
  if (!value) return "";

  const text = String(value).trim();

  // ISO format: 2026-10-13 or 2026-10-13T00:00:00
  const isoMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const date = new Date(Number(year), Number(month) - 1, Number(day));

    if (
      date.getFullYear() === Number(year) &&
      date.getMonth() === Number(month) - 1 &&
      date.getDate() === Number(day)
    ) {
      return `${year}-${month}-${day}`;
    }

    return "";
  }

  // API format: 13-10-2026 12:00:00 AM
  const dateMatch = text.match(
    /^(\d{2})-(\d{2})-(\d{4})(?:\s+\d{1,2}:\d{2}(?::\d{2})?\s*(?:AM|PM)?)?$/i,
  );

  if (dateMatch) {
    const [, day, month, year] = dateMatch;

    const date = new Date(Number(year), Number(month) - 1, Number(day));

    if (
      date.getFullYear() === Number(year) &&
      date.getMonth() === Number(month) - 1 &&
      date.getDate() === Number(day)
    ) {
      return `${year}-${month}-${day}`;
    }

    return "";
  }

  return "";
};

const formatDate = (value) => {
  const normalized = getDateValue(value);

  if (!normalized) return "-";

  const [year, month, day] = normalized.split("-");

  return `${day}-${month}-${year}`;
};

const formatTime = (value) => {
  if (!value) return "";

  const [hourText, minuteText] = String(value).split(":");

  const hour = Number(hourText);

  if (
    !Number.isInteger(hour) ||
    hour < 0 ||
    hour > 23 ||
    minuteText === undefined
  ) {
    return String(value);
  }

  const suffix = hour >= 12 ? "PM" : "AM";

  const formattedHour = hour % 12 || 12;

  return `${formattedHour}:${minuteText.slice(0, 2)} ${suffix}`;
};

const getShiftDurationMinutes = (shift) => {
  if (!shift) return null;

  const startTime = shift.firstHalfInTime || shift.shiftStartTime;

  const endTime = shift.secondHalfOutTime || shift.shiftEndTime;

  if (!startTime || !endTime) return null;

  const toMinutes = (time) => {
    const parts = String(time).split(":");
    const hours = Number(parts[0]);
    const minutes = Number(parts[1]);

    if (
      !Number.isInteger(hours) ||
      !Number.isInteger(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }

    return hours * 60 + minutes;
  };

  const start = toMinutes(startTime);
  const end = toMinutes(endTime);

  if (start === null || end === null || start === end) {
    return null;
  }

  // Support overnight shifts.
  const duration = end > start ? end - start : 1440 - start + end;

  return duration;
};

/* =========================================================

   MAP SHIFT API RESPONSE

   Field aliases match the existing Shift Master response.

========================================================= */

const mapApiShift = (item) => ({
  id: String(getShiftId(item) ?? ""),

  departmentId: String(getDepartmentId(item) ?? ""),

  departmentName: getDepartmentName(item),

  shiftNumber: String(item?.shiftNumber ?? item?.ShiftNumber ?? ""),

  shiftName: String(item?.shiftName ?? item?.ShiftName ?? ""),

  shiftHours: item?.shiftHours ?? item?.ShiftHours ?? "",

  shiftType: item?.shiftType ?? item?.ShiftType ?? "",

  firstHalfInTime: item?.firstHalfInTime ?? item?.FirstHalfInTime ?? "",

  secondHalfOutTime: item?.secondHalfOutTime ?? item?.SecondHalfOutTime ?? "",

  isActive: Number(item?.isActive ?? item?.IsActive ?? 1) === 1,
});

/* =========================================================

   MAP RULE API RESPONSE

   The mapper accepts camelCase and PascalCase response fields.

========================================================= */

const mapApiRule = (item) => {
  const activeValue = item?.isActive ?? item?.IsActive;

  return {
    id: String(getRuleId(item) ?? ""),

    departmentId: String(getDepartmentId(item) ?? ""),

    shiftId: String(
      item?.shiftId ??
        item?.ShiftId ??
        item?.shiftMasterId ??
        item?.ShiftMasterId ??
        "",
    ),

    departmentName: getDepartmentName(item),

    shiftName: String(item?.shiftName ?? item?.ShiftName ?? ""),

    lateMinutes: Number(item?.lateMinutes ?? item?.LateMinutes ?? 0),

    minimumFullDayMinutes: Number(
      item?.minimumFullDayMinutes ?? item?.MinimumFullDayMinutes ?? 0,
    ),

    minimumHalfDayMinutes: Number(
      item?.minimumHalfDayMinutes ?? item?.MinimumHalfDayMinutes ?? 0,
    ),

    shortLeaveMinutes: Number(
      item?.shortLeaveMinutes ?? item?.ShortLeaveMinutes ?? 0,
    ),

    overtimeAfterMinutes: Number(
      item?.overtimeAfterMinutes ?? item?.OvertimeAfterMinutes ?? 0,
    ),

    effectiveFrom: getDateValue(
      item?.effectiveFrom ??
        item?.EffectiveFrom ??
        item?.effectiveFromDate ??
        item?.EffectiveFromDate,
    ),

    effectiveTo: getDateValue(
      item?.effectiveTo ??
        item?.EffectiveTo ??
        item?.effectiveToDate ??
        item?.EffectiveToDate,
    ),

    isActive:
      activeValue == null
        ? true
        : activeValue === true ||
          activeValue === 1 ||
          activeValue === "1" ||
          String(activeValue).toLowerCase() === "true",
  };
};

/* =========================================================
   EMPLOYEE LOOKUP + EXECUTION RESPONSE HELPERS
========================================================= */

const getLocalDateString = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const mapEmployee = (employee) => ({
  id: String(
    employee?.personID ??
      employee?.PersonID ??
      employee?.personId ??
      employee?.PersonId ??
      employee?.userID ??
      employee?.UserID ??
      employee?.empId ??
      employee?.EmpId ??
      employee?.id ??
      employee?.Id ??
      "",
  ),
  name: String(
    employee?.buddyName ??
      employee?.employeeName ??
      employee?.EmployeeName ??
      employee?.empName ??
      employee?.EmpName ??
      employee?.personName ??
      employee?.PersonName ??
      employee?.name ??
      employee?.Name ??
      "",
  ).trim(),
  organization: String(
    employee?.organization ?? employee?.Organization ?? "",
  ).trim(),
});

const getExecutionData = (response) => {
  let value =
    response?.data?.data ??
    response?.data?.Data ??
    response?.data?.result ??
    response?.data?.Result ??
    response?.data;
  if (Array.isArray(value)) value = value[0] ?? {};
  if (value && typeof value === "object") {
    const nested = value.executionResult ?? value.ExecutionResult;
    if (nested && typeof nested === "object") value = nested;
  }
  return value && typeof value === "object" ? value : {};
};

const getResultField = (source, ...keys) => {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null)
      return source[key];
  }
  return "";
};

const formatMinutes = (value) => {
  if (value === "" || value === null || value === undefined) return "—";
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${numeric} min` : String(value);
};

/* =========================================================

   EFFECTIVE DATE OVERLAP

   An empty Effective To is treated as an ongoing rule.

========================================================= */

const dateRangesOverlap = (firstFrom, firstTo, secondFrom, secondTo) => {
  const firstStart = getDateValue(firstFrom);

  const secondStart = getDateValue(secondFrom);

  const firstEnd = getDateValue(firstTo) || "9999-12-31";

  const secondEnd = getDateValue(secondTo) || "9999-12-31";

  return firstStart <= secondEnd && secondStart <= firstEnd;
};

/* =========================================================

   VALIDATION

========================================================= */

const minutesSchema = Yup.number()

  .transform((value, originalValue) =>
    originalValue === "" ? undefined : value,
  )

  .typeError("Enter a valid number of minutes")

  .integer("Minutes must be a whole number")

  .min(0, "Minutes cannot be negative")

  .required("This field is required");

const validationSchema = Yup.object({
  departmentId: Yup.string().required("Department is required"),

  shiftId: Yup.string().required("Shift is required"),

  lateMinutes: minutesSchema,

  minimumFullDayMinutes: minutesSchema,

  minimumHalfDayMinutes: minutesSchema,

  shortLeaveMinutes: minutesSchema,

  overtimeAfterMinutes: minutesSchema,

  effectiveFrom: Yup.string()

    .required("Effective From is required")

    .test("valid-date", "Enter a valid date", (value) => {
      return !value || /^\d{4}-\d{2}-\d{2}$/.test(value);
    }),

  effectiveTo: Yup.string()

    .nullable()

    .test(
      "valid-end-date",

      "Enter a valid Effective To date",

      (value) => !value || /^\d{4}-\d{2}-\d{2}$/.test(value),
    )

    .test(
      "end-after-start",

      "Effective To cannot be earlier than Effective From",

      function (value) {
        const { effectiveFrom } = this.parent;

        if (!value || !effectiveFrom) return true;

        return value >= effectiveFrom;
      },
    ),
});

/* =========================================================

   COMPONENT

========================================================= */

const ShiftWiseRuleConfiguration = () => {
  const [departments, setDepartments] = useState([]);

  const [shifts, setShifts] = useState([]);

  const [list, setList] = useState([]);

  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [testEmployeeId, setTestEmployeeId] = useState("");
  const [testAttendanceDate, setTestAttendanceDate] = useState(
    getLocalDateString(),
  );
  const [testRuleId, setTestRuleId] = useState("");
  const [executing, setExecuting] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);

  const [editId, setEditId] = useState(null);

  const [currentStatus, setCurrentStatus] = useState(true);

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [order, setOrder] = useState("asc");

  const [orderBy, setOrderBy] = useState("departmentName");

  const [globalSearch, setGlobalSearch] = useState("");

  const [searchText, setSearchText] = useState({});

  const [showAllFilters, setShowAllFilters] = useState(false);

  /* =======================================================

     BREADCRUMB

  ======================================================= */

  const BCrumb = [
    {
      to: "/",

      title: "ASAI Admin",
    },

    {
      title: "Shift-Wise Rule Configuration",
    },
  ];

  /* =======================================================

     FETCH DEPARTMENTS

  ======================================================= */

  const fetchDepartments = async () => {
    try {
      const response = await GetAllDepartments();

      if (response?.status === 200) {
        const rows = getResponseRows(response);

        setDepartments(rows);
      } else {
        setDepartments([]);

        toast.error("Unable to load departments.");
      }
    } catch (error) {
      console.error("Failed to load departments:", error);

      toast.error(
        getRequestErrorMessage(
          error,

          "Unable to load departments. Please try again.",
        ),
      );
    }
  };

  /* =======================================================

     FETCH SHIFTS

     Uses the existing Shift Master API.

  ======================================================= */

  const fetchShifts = async () => {
    try {
      const response = await AddOrEditShift({
        action: "GET",
      });

      const rows = getResponseRows(response);

      setShifts(rows.map(mapApiShift));
    } catch (error) {
      console.error("Failed to load shifts:", error);

      toast.error(
        getRequestErrorMessage(
          error,

          "Unable to load shifts. Please try again.",
        ),
      );
    }
  };

  /* =======================================================

     FETCH RULE CONFIGURATIONS

  ======================================================= */

  const fetchRules = async () => {
    try {
      setLoading(true);

      const response = await AddOrEditShiftWiseRuleConfiguration({
        action: "GET",
      });

      const rows = getResponseRows(response);

      setList(rows.map(mapApiRule));
    } catch (error) {
      console.error("Failed to load shift-wise rules:", error);

      toast.error(
        getRequestErrorMessage(
          error,

          "Unable to load shift-wise rule configurations.",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================

     FETCH EMPLOYEES FOR RULE TESTING

  ======================================================= */

  const fetchEmployees = async () => {
    try {
      setEmployeesLoading(true);
      const response = await GetAllEmployeeDetailsDropdowns(true);
      const data = response?.data;
      const employeeRows =
        data?.buddyDropDown ??
        data?.data?.buddyDropDown ??
        data?.result?.buddyDropDown ??
        data?.Result?.buddyDropDown ??
        [];
      const mapped = Array.isArray(employeeRows)
        ? employeeRows
            .map(mapEmployee)
            .filter((employee) => employee.id && employee.name)
        : [];
      setEmployees(
        Array.from(
          new Map(mapped.map((employee) => [employee.id, employee])).values(),
        ),
      );
    } catch (error) {
      console.error("Failed to load employees for rule testing:", error);
      toast.error(
        getRequestErrorMessage(
          error,
          "Unable to load employees for rule testing.",
        ),
      );
    } finally {
      setEmployeesLoading(false);
    }
  };

  /* =======================================================

     INITIAL LOAD

  ======================================================= */

  useEffect(() => {
    fetchDepartments();
    fetchShifts();
    fetchRules();
    fetchEmployees();
  }, []);

  /* =======================================================

     DEPARTMENT AND SHIFT LOOKUPS

  ======================================================= */

  const getDepartmentLabel = (departmentId, fallback = "") => {
    const department = departments.find(
      (item) => String(getDepartmentOptionId(item)) === String(departmentId),
    );

    return getDepartmentName(department) || fallback || "-";
  };

  const getShiftLabel = (shiftId, fallback = "") => {
    const shift = shifts.find((item) => String(item.id) === String(shiftId));

    if (!shift) {
      return fallback || (shiftId ? `Shift ${shiftId}` : "-");
    }

    if (shift.shiftName) return shift.shiftName;

    const number = shift.shiftNumber ? `Shift ${shift.shiftNumber}` : "Shift";

    const start = formatTime(shift.firstHalfInTime);

    const end = formatTime(shift.secondHalfOutTime);

    if (start && end) {
      return `${number} (${start} - ${end})`;
    }

    if (shift.shiftType) {
      return `${number} - ${shift.shiftType}`;
    }

    return number;
  };

  /*

   * The actual form state is declared below. This comment

   * is replaced by the working shift filter immediately

   * after Formik initialization.

   */

  /* =======================================================

     FORM

  ======================================================= */

  const formik = useFormik({
    initialValues: EMPTY_FORM,

    validationSchema,

    onSubmit: async (values, { resetForm }) => {
      try {
        const isEditing = editId !== null;

        const selectedDepartment = departments.find(
          (item) =>
            String(getDepartmentOptionId(item)) === String(values.departmentId),
        );

        const selectedShift = shifts.find(
          (item) => String(item.id) === String(values.shiftId),
        );

        if (!selectedDepartment) {
          toast.error("Select a valid department.");

          return;
        }

        if (
          !selectedShift ||
          String(selectedShift.departmentId) !== String(values.departmentId)
        ) {
          toast.error("Select a valid shift for the selected department.");

          return;
        }

        /*

         * Do not allow two ACTIVE rules for the same

         * department + shift over overlapping date ranges.

         */

        const hasOverlap = list.some(
          (item) =>
            item.isActive &&
            currentStatus &&
            String(item.departmentId) === String(values.departmentId) &&
            String(item.shiftId) === String(values.shiftId) &&
            String(item.id) !== String(editId) &&
            dateRangesOverlap(
              item.effectiveFrom,

              item.effectiveTo,

              values.effectiveFrom,

              values.effectiveTo,
            ),
        );

        if (hasOverlap) {
          toast.error(
            "An active rule already exists for this department and shift during the selected effective period.",
          );

          return;
        }

        const payload = {
          action: isEditing ? "UPDATE" : "INSERT",

          id: isEditing ? Number(editId) : 0,

          departmentId: Number(values.departmentId),

          shiftId: Number(values.shiftId),

          lateMinutes: Number(values.lateMinutes),

          minimumFullDayMinutes: Number(values.minimumFullDayMinutes),

          minimumHalfDayMinutes: Number(values.minimumHalfDayMinutes),

          shortLeaveMinutes: Number(values.shortLeaveMinutes),

          overtimeAfterMinutes: Number(values.overtimeAfterMinutes),

          effectiveFrom: values.effectiveFrom,

          effectiveTo: values.effectiveTo || null,

          isActive: currentStatus ? 1 : 0,
        };

        setSaving(true);

        const response = await AddOrEditShiftWiseRuleConfiguration(payload);

        const result = getApiResult(response);

        if (isApiFailure(result)) {
          toast.error(
            getApiMessage(result) ||
              `Unable to ${isEditing ? "update" : "add"} rule configuration.`,
          );

          return;
        }

        await fetchRules();

        toast.success(
          getApiMessage(result) ||
            (isEditing
              ? "Shift-wise rule updated successfully."
              : "Shift-wise rule added successfully."),
        );

        resetForm({ values: EMPTY_FORM });

        setEditId(null);

        setCurrentStatus(true);

        setPage(0);
      } catch (error) {
        console.error("Failed to save shift-wise rule:", error);

        toast.error(
          getRequestErrorMessage(
            error,

            `Unable to ${
              editId !== null ? "update" : "add"
            } the rule configuration.`,
          ),
        );
      } finally {
        setSaving(false);
      }
    },
  });

  /*

   * Department changes should clear the selected shift.

   * The options are always limited to shifts belonging

   * to the selected department.

   */

  const filteredShiftsForForm = useMemo(() => {
    if (!formik.values.departmentId) return [];

    return shifts.filter(
      (item) =>
        String(item.departmentId) === String(formik.values.departmentId),
    );
  }, [shifts, formik.values.departmentId]);

  /* =======================================================

     RESET FORM

  ======================================================= */

  const handleReset = () => {
    setEditId(null);

    setCurrentStatus(true);

    formik.resetForm({
      values: EMPTY_FORM,
    });
  };

  /* =======================================================

     EDIT RECORD

  ======================================================= */

  const handleEdit = (item) => {
    setEditId(item.id);

    setCurrentStatus(item.isActive);

    formik.setValues({
      departmentId: String(item.departmentId ?? ""),

      shiftId: String(item.shiftId ?? ""),

      lateMinutes: String(item.lateMinutes ?? 0),

      minimumFullDayMinutes: String(item.minimumFullDayMinutes ?? 0),

      minimumHalfDayMinutes: String(item.minimumHalfDayMinutes ?? 0),

      shortLeaveMinutes: String(item.shortLeaveMinutes ?? 0),

      overtimeAfterMinutes: String(item.overtimeAfterMinutes ?? 0),

      effectiveFrom: getDateValue(item.effectiveFrom),

      effectiveTo: getDateValue(item.effectiveTo),
    });

    window.scrollTo({
      top: 0,

      behavior: "smooth",
    });
  };

  /* =======================================================

     ACTIVE / INACTIVE STATUS

     Follows the existing Shift Master pattern:

     UPDATE the ID and IsActive without DELETE.

  ======================================================= */

  const handleStatusChange = async (item, checked) => {
    try {
      setStatusUpdatingId(String(item.id));

      const response = await AddOrEditShiftWiseRuleConfiguration({
        action: "UPDATE",

        id: Number(item.id),

        isActive: checked ? 1 : 0,
      });

      const result = getApiResult(response);

      if (isApiFailure(result)) {
        toast.error(getApiMessage(result) || "Unable to update rule status.");

        return;
      }

      await fetchRules();

      toast.success(
        getApiMessage(result) ||
          (checked
            ? "Rule configuration activated successfully."
            : "Rule configuration deactivated successfully."),
      );
    } catch (error) {
      console.error("Failed to update rule status:", error);

      toast.error(
        getRequestErrorMessage(
          error,

          "Unable to update rule status. Please try again.",
        ),
      );
    } finally {
      setStatusUpdatingId(null);
    }
  };

  /* =======================================================

     RULES VALID FOR THE TEST DATE

  ======================================================= */

  const testableRules = useMemo(() => {
    if (!testAttendanceDate) return [];
    return list.filter((rule) => {
      const startsOnOrBefore =
        !rule.effectiveFrom || rule.effectiveFrom <= testAttendanceDate;
      const endsOnOrAfter =
        !rule.effectiveTo || rule.effectiveTo >= testAttendanceDate;
      return rule.isActive && startsOnOrBefore && endsOnOrAfter;
    });
  }, [list, testAttendanceDate]);

  useEffect(() => {
    if (
      testRuleId &&
      !testableRules.some((rule) => String(rule.id) === String(testRuleId))
    ) {
      setTestRuleId("");
      setExecutionResult(null);
    }
  }, [testRuleId, testableRules]);

  /* =======================================================

     TABLE VALUE HELPERS

  ======================================================= */

  const getRowValue = (item, field) => {
    switch (field) {
      case "departmentName":
        return getDepartmentLabel(
          item.departmentId,

          item.departmentName,
        );

      case "shiftName":
        return getShiftLabel(
          item.shiftId,

          item.shiftName,
        );

      case "effectiveFrom":

      case "effectiveTo":
        return formatDate(item[field]);

      default:
        return item[field] ?? "";
    }
  };

  /* =======================================================

     SEARCH + COLUMN FILTERS

  ======================================================= */

  const filteredList = useMemo(() => {
    const query = normalizeText(globalSearch);

    return list.filter((item) => {
      const searchableValues = [
        getDepartmentLabel(
          item.departmentId,

          item.departmentName,
        ),

        getShiftLabel(item.shiftId, item.shiftName),

        item.lateMinutes,

        item.minimumFullDayMinutes,

        item.minimumHalfDayMinutes,

        item.shortLeaveMinutes,

        item.overtimeAfterMinutes,

        item.effectiveFrom,

        item.effectiveTo,

        item.isActive ? "Active" : "Inactive",
      ];

      const globalMatch =
        !query || normalizeText(searchableValues.join(" ")).includes(query);

      const columnMatch = Object.entries(searchText).every(
        ([field, filter]) => {
          if (!filter) return true;

          return normalizeText(getRowValue(item, field)).includes(
            normalizeText(filter),
          );
        },
      );

      return globalMatch && columnMatch;
    });
  }, [list, globalSearch, searchText, departments, shifts]);

  /* =======================================================

     SORT

  ======================================================= */

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";

    setOrder(isAsc ? "desc" : "asc");

    setOrderBy(property);
  };

  const sortedList = useMemo(() => {
    return [...filteredList].sort((a, b) => {
      if (!orderBy) return 0;

      let comparison = 0;

      if (
        RULE_COLUMNS.some(
          (column) => column.field === orderBy && column.numeric,
        )
      ) {
        comparison = Number(a[orderBy] ?? 0) - Number(b[orderBy] ?? 0);
      } else {
        comparison = String(getRowValue(a, orderBy) ?? "").localeCompare(
          String(getRowValue(b, orderBy) ?? ""),

          undefined,

          {
            numeric: true,

            sensitivity: "base",
          },
        );
      }

      return order === "asc" ? comparison : -comparison;
    });
  }, [filteredList, order, orderBy, departments, shifts]);

  /* =======================================================
     PAGINATION
  ======================================================= */

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredList.length / rowsPerPage) - 1,
    );

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredList.length, rowsPerPage, page]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /* =======================================================
     RESET TABLE FILTERS
  ======================================================= */

  const handleResetFilters = () => {
    setGlobalSearch("");
    setSearchText({});
    setShowAllFilters(false);
    setPage(0);
  };

  /* =======================================================
     TABLE HEADER — MATCHES SHIFT MASTER
  ======================================================= */

  const renderHeader = (label, field) => (
    <TableCell key={field}>
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        sx={{ color: "text.primary" }}
      >
        <TableSortLabel
          active={orderBy === field}
          direction={orderBy === field ? order : "asc"}
          onClick={() => handleSort(field)}
        >
          {label}
        </TableSortLabel>
      </Box>
    </TableCell>
  );

  /* =======================================================
     MINUTES FIELD — MATCHES SHIFT MASTER SPACING
  ======================================================= */

  const renderMinutesField = (field, label) => {
    const hasError = formik.touched[field] && Boolean(formik.errors[field]);

    return (
      <Grid item lg={2.4} md={4} sm={6} xs={12} key={field}>
        <CustomFormLabel>{label}</CustomFormLabel>
        <CustomTextField
          fullWidth
          size="small"
          type="number"
          name={field}
          value={formik.values[field]}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(hasError)}
          helperText={hasError ? formik.errors[field] : undefined}
          inputProps={{
            min: 0,
            step: 1,
            inputMode: "numeric",
          }}
          InputProps={{
            endAdornment: <InputAdornment position="end">min</InputAdornment>,
          }}
        />
      </Grid>
    );
  };

  const selectedTestRule = testableRules.find(
    (rule) => String(rule.id) === String(testRuleId),
  );

  const executionFields = executionResult
    ? [
        {
          label: "Employee",
          value:
            getResultField(executionResult, "employeeName", "EmployeeName") ||
            employees.find((employee) => employee.id === testEmployeeId)
              ?.name ||
            testEmployeeId,
        },
        {
          label: "Attendance Date",
          value:
            getResultField(
              executionResult,
              "attendanceDate",
              "AttendanceDate",
              "date",
              "Date",
            ) || testAttendanceDate,
        },
        {
          label: "Department / Shift",
          value:
            getResultField(
              executionResult,
              "departmentName",
              "DepartmentName",
            ) ||
            `${getDepartmentLabel(
              selectedTestRule?.departmentId,
              selectedTestRule?.departmentName,
            )} / ${getShiftLabel(
              selectedTestRule?.shiftId,
              selectedTestRule?.shiftName,
            )}`,
        },
        {
          label: "Attendance Status",
          value:
            getResultField(
              executionResult,
              "attendanceStatus",
              "AttendanceStatus",
              "gender",
              "Gender",
            ) || "—",
        },
        {
          label: "Employee Check-in",
          value:
            getResultField(
              executionResult,
              "checkIn",
              "CheckIn",
              "checkin",
              "Checkin",
            ) || "—",
        },
        {
          label: "Employee Check-out",
          value:
            getResultField(
              executionResult,
              "checkOut",
              "CheckOut",
              "checkout",
              "Checkout",
            ) || "—",
        },
        {
          label: "Gross Working Minutes",
          value: formatMinutes(
            getResultField(
              executionResult,
              "grossWorkingMinutes",
              "GrossWorkingMinutes",
            ),
          ),
        },
        {
          label: "Lunch Out",
          value:
            getResultField(
              executionResult,
              "lunchOutTime",
              "LunchOutTime",
              "lunchOut",
              "LunchOut",
            ) || "—",
        },
        {
          label: "Lunch In",
          value:
            getResultField(
              executionResult,
              "lunchInTime",
              "LunchInTime",
              "lunchIn",
              "LunchIn",
            ) || "—",
        },
        {
          label: "Lunch Duration",
          value: formatMinutes(
            getResultField(executionResult, "lunchMinutes", "LunchMinutes"),
          ),
        },
        {
          label: "Net Working Minutes",
          value: formatMinutes(
            getResultField(
              executionResult,
              "netWorkingMinutes",
              "NetWorkingMinutes",
              "totalWorkingMinutes",
              "TotalWorkingMinutes",
            ),
          ),
        },
        {
          label: "Late Minutes",
          value: formatMinutes(
            getResultField(
              executionResult,
              "calculatedLateMinutes",
              "CalculatedLateMinutes",
              "lateMinutes",
              "LateMinutes",
            ),
          ),
        },
        {
          label: "Early Minutes",
          value: formatMinutes(
            getResultField(executionResult, "earlyMinutes", "EarlyMinutes"),
          ),
        },
        {
          label: "Short Leave Minutes",
          value: formatMinutes(
            getResultField(
              executionResult,
              "calculatedShortLeaveMinutes",
              "CalculatedShortLeaveMinutes",
              "shortLeaveMinutes",
              "ShortLeaveMinutes",
            ),
          ),
        },
        {
          label: "Overtime Minutes",
          value: formatMinutes(
            getResultField(
              executionResult,
              "overtimeMinutes",
              "OvertimeMinutes",
            ),
          ),
        },
      ]
    : [];

  const visibleRows = sortedList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  /* =======================================================
     RENDER — SHIFT MASTER VISUAL STYLE
  ======================================================= */

  return (
    <PageContainer title="Shift Rule Configuration">
      <Breadcrumb title="Shift Rule Configuration" items={BCrumb} />

      <Box>
        {/* =================================================
            ADD / EDIT RULE
        ================================================= */}
        <Card
          sx={{
            mb: 2,
            p: 0,
            overflow: "hidden",
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            px={2}
            py={1.5}
            sx={{
              backgroundColor: "primary.light",
              color: "primary.main",
            }}
          >
            <FeatherIcon icon="clock" width="18" />
            <Box sx={{ ml: 1 }}>
              {editId !== null
                ? "Edit Shift Rule Configuration"
                : "Add Shift Rule Configuration"}
            </Box>
          </Box>

          <Box component="form" onSubmit={formik.handleSubmit}>
            <CardContent sx={{ padding: "10px 24px" }}>
              <Grid container spacing={2} alignItems="flex-start">
                {/* DEPARTMENT */}
                <Grid item lg={2.4} md={4} sm={6} xs={12}>
                  <CustomFormLabel>Department</CustomFormLabel>
                  <CustomTextField
                    select
                    name="departmentId"
                    value={formik.values.departmentId}
                    onChange={(event) => {
                      formik.handleChange(event);

                      formik.setFieldValue("shiftId", "");

                      // Clear thresholds from the previous shift.
                      formik.setFieldValue("minimumFullDayMinutes", "");
                      formik.setFieldValue("minimumHalfDayMinutes", "");
                    }}
                    onBlur={formik.handleBlur}
                    fullWidth
                    size="small"
                    error={
                      formik.touched.departmentId &&
                      Boolean(formik.errors.departmentId)
                    }
                    helperText={
                      formik.touched.departmentId && formik.errors.departmentId
                    }
                    SelectProps={{
                      MenuProps: {
                        PaperProps: { sx: { maxHeight: 300 } },
                      },
                    }}
                  >
                    <MenuItem value="">Select Department</MenuItem>
                    {departments.map((department) => {
                      const id = getDepartmentOptionId(department);
                      const name = getDepartmentName(department);
                      if (!id || !name) return null;

                      return (
                        <MenuItem key={id} value={String(id)}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </CustomTextField>
                </Grid>

                {/* SHIFT */}
                <Grid item lg={2.4} md={4} sm={6} xs={12}>
                  <CustomFormLabel>Shift</CustomFormLabel>
                  <CustomTextField
                    select
                    name="shiftId"
                    value={formik.values.shiftId}
                    onChange={(event) => {
                      const shiftId = event.target.value;

                      formik.setFieldValue("shiftId", shiftId);

                      const selectedShift = shifts.find(
                        (shift) => String(shift.id) === String(shiftId),
                      );

                      const duration = getShiftDurationMinutes(selectedShift);

                      if (duration !== null) {
                        formik.setFieldValue(
                          "minimumFullDayMinutes",
                          String(duration),
                        );

                        formik.setFieldValue(
                          "minimumHalfDayMinutes",
                          String(Math.floor(duration / 2)),
                        );
                      } else {
                        formik.setFieldValue("minimumFullDayMinutes", "");
                        formik.setFieldValue("minimumHalfDayMinutes", "");
                      }
                    }}
                    onBlur={formik.handleBlur}
                    fullWidth
                    size="small"
                    disabled={!formik.values.departmentId}
                    error={
                      formik.touched.shiftId && Boolean(formik.errors.shiftId)
                    }
                    helperText={formik.touched.shiftId && formik.errors.shiftId}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: { sx: { maxHeight: 300 } },
                      },
                    }}
                  >
                    <MenuItem value="">Select Shift</MenuItem>
                    {filteredShiftsForForm.map((shift) => (
                      <MenuItem key={shift.id} value={String(shift.id)}>
                        {getShiftLabel(shift.id)}
                        {!shift.isActive ? " (Inactive)" : ""}
                      </MenuItem>
                    ))}
                  </CustomTextField>
                </Grid>

                {renderMinutesField("lateMinutes", "Late Minutes")}
                {renderMinutesField(
                  "minimumFullDayMinutes",
                  "Minimum Full Day Minutes",
                )}
                {renderMinutesField(
                  "minimumHalfDayMinutes",
                  "Minimum Half Day Minutes",
                )}
                {renderMinutesField("shortLeaveMinutes", "Short Leave Minutes")}
                {renderMinutesField(
                  "overtimeAfterMinutes",
                  "Overtime After Minutes",
                )}

                {/* EFFECTIVE FROM */}
                <Grid item lg={2.4} md={4} sm={6} xs={12}>
                  <CustomFormLabel>Effective From</CustomFormLabel>
                  <CustomTextField
                    fullWidth
                    type="date"
                    size="small"
                    name="effectiveFrom"
                    value={formik.values.effectiveFrom}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    InputLabelProps={{ shrink: true }}
                    error={
                      formik.touched.effectiveFrom &&
                      Boolean(formik.errors.effectiveFrom)
                    }
                    helperText={
                      formik.touched.effectiveFrom &&
                      formik.errors.effectiveFrom
                    }
                  />
                </Grid>

                {/* EFFECTIVE TO */}
                <Grid item lg={2.4} md={4} sm={6} xs={12}>
                  <CustomFormLabel>Effective To</CustomFormLabel>
                  <CustomTextField
                    fullWidth
                    type="date"
                    size="small"
                    name="effectiveTo"
                    value={formik.values.effectiveTo}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      min: formik.values.effectiveFrom || undefined,
                    }}
                    error={
                      formik.touched.effectiveTo &&
                      Boolean(formik.errors.effectiveTo)
                    }
                    helperText={
                      formik.touched.effectiveTo && formik.errors.effectiveTo
                    }
                  />
                </Grid>

                {/* STATUS */}
                <Grid item lg={2.4} md={4} sm={6} xs={12}>
                  <CustomFormLabel>Configuration Status</CustomFormLabel>
                  <Box
                    sx={{
                      minHeight: 40,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <FormControlLabel
                      sx={{ ml: 0 }}
                      control={
                        <Switch
                          checked={currentStatus}
                          onChange={(event) =>
                            setCurrentStatus(event.target.checked)
                          }
                          color="primary"
                          size="small"
                        />
                      }
                      label={currentStatus ? "Active" : "Inactive"}
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* BUTTONS — LEFT ALIGNED LIKE SHIFT MASTER */}
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={saving}
                    startIcon={
                      saving ? (
                        <CircularProgress size={15} color="inherit" />
                      ) : undefined
                    }
                  >
                    {saving ? "Saving..." : editId !== null ? "Update" : "Add"}
                  </Button>
                  <Button
                    sx={{ ml: 2 }}
                    variant="outlined"
                    type="button"
                    onClick={handleReset}
                    disabled={saving}
                  >
                    {editId !== null ? "Cancel" : "Reset"}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Box>
        </Card>

        <Card
          sx={{
            mt: 2,
            p: 0,
            overflow: "hidden",
          }}
        >
          {/* LIST HEADER — SAME AS SHIFT MASTER */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            px={2}
            py={1.5}
            sx={{
              backgroundColor: "primary.light",
              color: "primary.main",
            }}
          >
            <Box display="flex" alignItems="center">
              <FeatherIcon icon="alert-circle" width="18" />
              <Box sx={{ ml: 1 }}>Existing Shift Rules</Box>
            </Box>

            {/* SEARCH + FILTER */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              flexWrap="wrap"
              gap={1}
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              <TextField
                size="small"
                placeholder="Search..."
                value={globalSearch}
                onFocus={() => {
                  setShowAllFilters(false);
                  setSearchText({});
                }}
                onChange={(event) => {
                  setGlobalSearch(event.target.value);
                  setPage(0);
                }}
                sx={{
                  width: { xs: "100%", sm: 220 },
                  minWidth: { sm: 180 },
                  backgroundColor: "white",
                  borderRadius: 1,
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ mr: 1 }}>
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              {globalSearch && (
                <CloseIcon
                  sx={{ cursor: "pointer", ml: 0.25 }}
                  onClick={() => {
                    setGlobalSearch("");
                    setPage(0);
                  }}
                />
              )}

              <FilterAltIcon
                sx={{
                  cursor: "pointer",
                  ml: 1,
                  color: showAllFilters ? "primary.main" : "text.primary",
                  transition: "0.2s",
                }}
                onClick={() => setShowAllFilters(!showAllFilters)}
              />

              <Button
                variant="outlined"
                size="small"
                sx={{ ml: 1, height: 32, whiteSpace: "nowrap" }}
                onClick={handleResetFilters}
              >
                Reset Filter
              </Button>
            </Box>
          </Box>

          <CardContent sx={{ p: 2 }}>
            {loading ? (
              <Box
                sx={{
                  minHeight: 140,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                <CircularProgress size={26} />
                <Typography variant="body2" color="text.secondary">
                  Loading rule configurations...
                </Typography>
              </Box>
            ) : filteredList.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 1 }}>
                {globalSearch.trim() ||
                Object.values(searchText).some((value) => String(value ?? "").trim())
                  ? "No matching rule configurations found."
                  : "No Rule Configurations Found"}
              </Typography>
            ) : (
              <>
                <TableContainer
                  component={Paper}
                  sx={{
                    width: "100%",
                    overflowX: "auto",
                  }}
                >
                  <Table
                    aria-label="Shift-wise rule configurations"
                    sx={{
                      minWidth: 1500,
                      "& .MuiTableCell-root": {
                        whiteSpace: "nowrap",
                        verticalAlign: "middle",
                      },
                    }}
                  >
                    <TableHead>
                      {/* MAIN HEADER */}
                      <TableRow
                        sx={{
                          backgroundColor: "#FAFBFB",
                          "& .MuiTableCell-root": {
                            position: "sticky",
                            top: 0,
                            zIndex: 4,
                            backgroundColor: "#FAFBFB",
                          },
                        }}
                      >
                        {RULE_COLUMNS.map((column) =>
                          renderHeader(column.label, column.field),
                        )}
                        <TableCell align="center">Action</TableCell>
                      </TableRow>

                      {/* FILTER ROW */}
                      {showAllFilters && (
                        <TableRow
                          sx={{
                            backgroundColor: "#FFFFFF",
                            "& .MuiTableCell-root": {
                              padding: "6px 8px",
                              height: 42,
                              verticalAlign: "middle",
                            },
                          }}
                        >
                          {RULE_COLUMNS.map((column) => (
                            <TableCell key={column.field}>
                              <TextField
                                size="small"
                                variant="outlined"
                                placeholder="Search..."
                                value={searchText[column.field] || ""}
                                onChange={(event) => {
                                  setSearchText((previous) => ({
                                    ...previous,
                                    [column.field]: event.target.value,
                                  }));
                                  setPage(0);
                                }}
                                sx={{
                                  minWidth: 85,
                                  backgroundColor: "white",
                                  "& .MuiInputBase-root": {
                                    height: 32,
                                    fontSize: 13,
                                  },
                                  "& .MuiInputBase-input": {
                                    padding: "6px 8px",
                                  },
                                }}
                              />
                            </TableCell>
                          ))}
                          <TableCell />
                        </TableRow>
                      )}
                    </TableHead>

                    <TableBody>
                      {visibleRows.map((item) => (
                        <TableRow
                          hover
                          key={
                            item.id ||
                            `${item.departmentId}-${item.shiftId}-${item.effectiveFrom}`
                          }
                        >
                          {RULE_COLUMNS.map((column) => (
                            <TableCell key={column.field}>
                              {column.numeric
                                ? item[column.field] ?? 0
                                : getRowValue(item, column.field)}
                            </TableCell>
                          ))}

                          <TableCell align="center">
                            <Box
                              display="flex"
                              justifyContent="center"
                              alignItems="center"
                              gap={1}
                            >
                              <Tooltip
                                title={item.isActive ? "Active" : "Inactive"}
                              >
                                <span>
                                  <Switch
                                    checked={item.isActive}
                                    size="small"
                                    disabled={
                                      statusUpdatingId === String(item.id)
                                    }
                                    onChange={(event) =>
                                      handleStatusChange(
                                        item,
                                        event.target.checked,
                                      )
                                    }
                                  />
                                </span>
                              </Tooltip>

                              <Tooltip title="Edit">
                                <EditIcon
                                  sx={{
                                    cursor: "pointer",
                                    color: "primary.main",
                                  }}
                                  onClick={() => handleEdit(item)}
                                />
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={filteredList.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  showFirstButton
                  showLastButton
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </PageContainer>
  );
};

export default ShiftWiseRuleConfiguration;
